/**
 * The upload step of an MCE adapter: pick the product and the files, and the
 * browser checks and builds them with the same converter LiftCloud runs. What
 * the upload built goes to the adapter form, which saves it; a server rebuild
 * stays the verdict on save.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Select, Spin, Table, Upload } from "antd";
import type { RcFile } from "antd/lib/upload";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IdlResultT } from "../../../../flatbuffers/generated/mce/idl-result";
import { ProductType } from "../../../../flatbuffers/generated/mce/product-type";
import { buildMceIdl } from "../../../../utils/mceIdl/client";
import { downloadMceIdlLog } from "../../../../utils/mceIdl/mceIdlLog";
import { MceIdlSources, MceIdlUploadFile } from "../../../../utils/mceIdl/messages";
import sortMceIdlFiles from "../../../../utils/mceIdl/sortFiles";
import { diagnosticRows, MceSourceNames } from "../../../../utils/mceIdl/tableView";
import pin from "../../../../wasm/mce_idl/pin.json";
import { DIAGNOSTIC_COLUMNS } from "./MceMappingReview";

const COULD_NOT_CHECK =
  "The files could not be checked. Reload the page and try again. If this continues, click Get log and send the file to technical support.";

const MEGABYTE = 1024 * 1024;

const PRODUCTS = Object.entries(ProductType)
  .filter(([, value]) => typeof value === "number")
  .map(([label, value]) => ({ value: value as number, label }));

export interface MceUploadOutcome {
  cdoBinary: Uint8Array;
  product: ProductType;
  result: IdlResultT;
  names: MceSourceNames;
}

const asMegabytes = (bytes: number) => `${bytes / MEGABYTE} MB`;

const isEventsFile = (name: string) => name.toLowerCase().endsWith(".xml");

/** What a file counts towards the IDL bundle: its contents and its name, as the converter counts. */
export function bundleBytes(name: string, size: number): number {
  return isEventsFile(name) ? 0 : size + new TextEncoder().encode(name).length;
}

/** Why a file cannot join the upload, checked before it is read. Null when it can. */
export function refusalForSize(name: string, size: number, othersBytes: number): string | null {
  const limit = isEventsFile(name) ? pin.maxEventsBytes : pin.maxFileBytes;
  if (size > limit) return `${name} is too large. A file can be at most ${asMegabytes(limit)}.`;
  if (othersBytes + bundleBytes(name, size) > pin.maxBundleBytes) {
    return `${name} is too large to add. All the files together can be at most ${asMegabytes(
      pin.maxBundleBytes,
    )}.`;
  }
  return null;
}

function namesOf(sources: MceIdlSources): MceSourceNames {
  return {
    main: sources.main.name,
    controller: sources.controller?.name ?? "",
    includes: sources.includes.map((file) => file.name),
    eventsXml: sources.eventsXml?.name ?? "",
  };
}

interface MceIdlUploadProps {
  initialProduct?: ProductType | null;
  /** The form still holds a build from an earlier upload, which this form may not have made. */
  holdsBuild?: boolean;
  /** A build with no errors, or null once the files no longer give one. */
  onBuilt: (outcome: MceUploadOutcome | null) => void;
}

export default function MceIdlUpload({
  initialProduct = null,
  holdsBuild = false,
  onBuilt,
}: MceIdlUploadProps) {
  const [product, setProduct] = useState<ProductType | null>(initialProduct);
  const [files, setFiles] = useState<MceIdlUploadFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [checkError, setCheckError] = useState<unknown>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [sizeProblem, setSizeProblem] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ result: IdlResultT; names: MceSourceNames } | null>(
    null,
  );

  const reportBuilt = useRef(onBuilt);
  reportBuilt.current = onBuilt;
  const reportedBuild = useRef(false);
  const heldBuild = useRef(holdsBuild);
  heldBuild.current = holdsBuild;
  const currentFiles = useRef(files);
  currentFiles.current = files;

  const sorted = useMemo(() => (files.length > 0 ? sortMceIdlFiles(files) : null), [files]);

  useEffect(() => {
    let current = true;
    setProblem(null);
    setFailure(null);
    setCheckError(null);
    setBusy(false);
    // A form opened again with no files keeps the build it holds, but any new set of
    // files replaces it, even one made before the form was last closed.
    if (reportedBuild.current || (sorted && heldBuild.current)) reportBuilt.current(null);
    reportedBuild.current = false;
    if (!sorted || product === null) return undefined;
    if (sorted.kind === "problem") {
      setProblem(sorted.message);
      return undefined;
    }
    const { sources } = sorted;
    setBusy(true);
    buildMceIdl(product, sources)
      .then((result) => {
        if (!current) return;
        const names = namesOf(sources);
        if (result.cdoBinary.length === 0) {
          setFailure({ result, names });
          return;
        }
        reportedBuild.current = true;
        reportBuilt.current({
          cdoBinary: Uint8Array.from(result.cdoBinary),
          product,
          result,
          names,
        });
      })
      .catch((error) => {
        if (current) setCheckError(error);
      })
      .finally(() => {
        if (current) setBusy(false);
      });
    return () => {
      current = false;
    };
  }, [sorted, product]);

  /** Judge a whole selection at once: antd offers its files one by one in the same tick. */
  const addSelection = async (selection: RcFile[]) => {
    const names = new Set(selection.map((file) => file.name));
    const kept = currentFiles.current.filter((existing) => !names.has(existing.name));
    let used = kept.reduce((total, one) => total + bundleBytes(one.name, one.bytes.length), 0);
    const refusals: string[] = [];
    const accepted = selection.filter((file) => {
      const refused = refusalForSize(file.name, file.size, used);
      if (refused) refusals.push(refused);
      else used += bundleBytes(file.name, file.size);
      return !refused;
    });
    setSizeProblem(refusals.length > 0 ? refusals.join(" ") : null);
    const added = await Promise.all(
      accepted.map(async (file) => ({
        name: file.name,
        bytes: new Uint8Array(await file.arrayBuffer()),
      })),
    );
    if (added.length > 0) {
      setFiles((previous) => [
        ...previous.filter((existing) => !names.has(existing.name)),
        ...added,
      ]);
    }
  };

  const beforeUpload = (file: RcFile, selection: RcFile[]) => {
    if (file === selection[0]) addSelection(selection);
    return false;
  };

  return (
    <div className="mt-3" data-testid="mce-idl-upload">
      <div className="row">
        <div className="col-12 col-md-6">
          <span className="generalStyles__inputLabel">Product Type</span>
          <Select
            className="generalStyles__width100"
            size="large"
            aria-label="Product Type"
            placeholder="Choose the product"
            value={product ?? undefined}
            onChange={setProduct}
            options={PRODUCTS}
            style={{ marginTop: 8 }}
          />
        </div>
        <div className="col-12 col-md-6">
          <span className="generalStyles__inputLabel">IDL and events files</span>
          <div style={{ marginTop: 8 }}>
            <Upload
              multiple
              accept=".idl,.xml"
              fileList={files.map((file) => ({ uid: file.name, name: file.name }))}
              beforeUpload={beforeUpload}
              onRemove={(removed) => {
                setSizeProblem(null);
                setFiles((previous) => previous.filter((file) => file.name !== removed.name));
              }}
            >
              <Button size="large" icon={<FontAwesomeIcon icon={faUpload} className="me-2" />}>
                Upload MCE IDL
              </Button>
            </Upload>
          </div>
        </div>
      </div>
      {busy && <Spin className="mt-3" tip="Checking the files" />}
      {sizeProblem && <Alert className="mt-3" type="error" showIcon message={sizeProblem} />}
      {problem && <Alert className="mt-3" type="error" showIcon message={problem} />}
      {checkError !== null && (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          message={COULD_NOT_CHECK}
          action={
            <Button
              size="small"
              onClick={() =>
                downloadMceIdlLog({
                  when: new Date(),
                  action: "build",
                  product: product === null ? "" : ProductType[product],
                  files: files.map((file) => ({ name: file.name, bytes: file.bytes.length })),
                  error: checkError,
                })
              }
            >
              Get log
            </Button>
          }
        />
      )}
      {failure && (
        <div className="mt-3">
          <Alert
            type="error"
            showIcon
            message="These files cannot be used yet. Fix the errors below and upload them again."
            description={diagnosticRows(failure.result.diagnostics, failure.names)[0]?.message}
            action={
              <Button
                size="small"
                onClick={() =>
                  downloadMceIdlLog({
                    when: new Date(),
                    action: "build",
                    product: product === null ? "" : ProductType[product],
                    files: files.map((file) => ({ name: file.name, bytes: file.bytes.length })),
                    error: new Error(
                      diagnosticRows(failure.result.diagnostics, failure.names)
                        .map((row) => `${row.severity} ${row.code} ${row.where}: ${row.message}`)
                        .join("\n"),
                    ),
                  })
                }
              >
                Get log
              </Button>
            }
          />
          <Table
            className="mt-2"
            size="small"
            rowKey="key"
            columns={DIAGNOSTIC_COLUMNS}
            dataSource={diagnosticRows(failure.result.diagnostics, failure.names)}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            scroll={{ x: true }}
          />
        </div>
      )}
    </div>
  );
}
